/**
 * profileService.ts
 * User profile management service with Firestore integration
 */

import { doc, getDoc, getDocFromServer, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { authFetch } from './apiAuth';
import { getApiBaseUrl } from '../utils/apiConfig';
import { sanitizeChatMessages } from '../utils/messageSanitizer';
import type { MasterProfile } from '../types/profile';
import { 
  calculateIngredientScore, 
  generateIngredientReason,
  calculateRoutineScore,
  suggestMissingRoutineSteps,
  calculateConcernRoutineBoost,
  calculateSeasonalBoost,
  calculateAgeBoost,
} from './ingredientKnowledge';

/**
 * Converts various timestamp formats to Date object
 * Handles Firestore Timestamp, Date, string, or number formats
 * @param value - Timestamp in any format
 * @returns Date object or undefined if conversion fails
 */
function convertToDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  
  // Handle Firestore Timestamp (has toDate method)
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  
  // Handle Date object
  if (value instanceof Date) {
    return value;
  }
  
  // Handle ISO string or timestamp number
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  
  // Handle Firestore Timestamp-like object with seconds/nanoseconds
  if (typeof value === 'object' && value !== null && 'seconds' in value) {
    const ts = value as { seconds: number; nanoseconds?: number };
    return new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000);
  }
  
  return undefined;
}

/**
 * Default empty master profile structure
 * Used by ensureShape to guarantee all sections exist
 */
export const defaultMasterProfile: MasterProfile = {
  identity: { country: 'US', currency: 'USD' },
  consent: {},
  skin: {},
  logistics: {},
  history: {
    conversations: [],
    purchases: [],
    sampleFeedback: [],
    emailsSent: [],
  },
  safety: {},
  segment: {},
  meta: {},
};

/**
 * Ensures a profile object has all required sections
 * Deep merges input with defaults, ensuring no undefined sections
 * @param input - Raw profile data (can be partial or unknown)
 * @returns Complete MasterProfile with all sections
 */
export function ensureShape(input: unknown): MasterProfile {
  const parsed = (input ?? {}) as Partial<MasterProfile>;
  return {
    ...defaultMasterProfile,
    ...parsed,
    identity: { ...defaultMasterProfile.identity, ...parsed.identity },
    consent: { ...defaultMasterProfile.consent, ...parsed.consent },
    skin: { ...defaultMasterProfile.skin, ...parsed.skin },
    logistics: { ...defaultMasterProfile.logistics, ...parsed.logistics },
    history: { 
      ...defaultMasterProfile.history, 
      ...parsed.history,
      conversations: parsed.history?.conversations ?? defaultMasterProfile.history?.conversations ?? [],
      purchases: parsed.history?.purchases ?? defaultMasterProfile.history?.purchases ?? [],
      sampleFeedback: parsed.history?.sampleFeedback ?? defaultMasterProfile.history?.sampleFeedback ?? [],
      emailsSent: parsed.history?.emailsSent ?? defaultMasterProfile.history?.emailsSent ?? [],
    },
    safety: { ...defaultMasterProfile.safety, ...parsed.safety },
    segment: { ...defaultMasterProfile.segment, ...parsed.segment },
    meta: { ...defaultMasterProfile.meta, ...parsed.meta },
  };
}

/**
 * Ensure profile has valid region defaults.
 * If country is a supported region, keep it; otherwise default to US.
 */
export function enforceUSDefaults(profile: Partial<MasterProfile>): Partial<MasterProfile> {
  const SUPPORTED = ['US', 'KR', 'BR', 'MX', 'SG', 'CA', 'UK', 'DE', 'FR', 'IT', 'ES', 'NL', 'SE', 'PL'];
  const country = profile.identity?.country;
  if (country) {
    const normalized = country.trim().toUpperCase();
    if (SUPPORTED.includes(normalized)) {
      // Normalize to canonical ISO code so downstream detection is consistent
      return {
        ...profile,
        identity: { ...profile.identity, country: normalized },
      };
    }
  }
  return {
    ...profile,
    identity: { ...profile.identity, country: 'US', currency: 'USD' },
  };
}

/**
 * Response type from extractProfile API
 */
export interface ExtractProfileResponse {
  coppaBlock: boolean;
  conflicts: Array<{ path: string; old: unknown; new: unknown }>;
  profile: Partial<MasterProfile>;
  diff: Record<string, { old?: unknown; new: unknown }>;
  normalized?: MasterProfile;
}

/**
 * Extracts profile data from conversation messages via AI backend
 * Calls the /api/extractProfile Azure Function to analyze conversation
 * and extract structured profile information
 * 
 * @param params - Extraction parameters
 * @param params.messages - Array of conversation messages
 * @param params.currentProfile - Existing profile for context and conflict detection
 * @param params.language - Language code ('en' or 'ko', defaults to 'ko')
 * @returns Promise resolving to extraction result with profile updates
 */
export async function extractProfile(params: {
  messages: Array<{ sender: 'user' | 'bot'; text: string }>;
  currentProfile?: unknown;
  language?: string;
}): Promise<ExtractProfileResponse> {
  const apiUrl = getApiBaseUrl();
  
  const response = await authFetch(`${apiUrl}/extractProfile`, {
    method: 'POST',
    body: JSON.stringify({
      ...params,
      messages: sanitizeChatMessages(params.messages),
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`extractProfile API failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}

/**
 * Loads user profile from Firestore
 * 
 * @param userId - Firebase user ID
 * @param forceServer - If true, bypass IndexedDB cache and fetch from server.
 *   Default is TRUE to prevent stale cached data from showing incorrect values
 *   (e.g., wrong name in greeting). Falls back to cache if offline.
 *   Set to FALSE only when you explicitly want cached data for performance.
 * @returns Promise resolving to MasterProfile or null if not found
 */
export async function loadProfile(userId: string, forceServer: boolean = true): Promise<MasterProfile | null> {
  try {
    const docRef = doc(db, 'masterProfiles', userId);
    
    // Use getDocFromServer to bypass IndexedDB cache for fresh data
    // This prevents stale cached data from showing wrong names/values
    let docSnap;
    if (forceServer) {
      try {
        docSnap = await getDocFromServer(docRef);
      } catch (serverError) {
        // If server fetch fails (offline), fall back to cache
        console.warn('Server fetch failed, using cache:', serverError);
        docSnap = await getDoc(docRef);
      }
    } else {
      docSnap = await getDoc(docRef);
    }

    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Convert Firestore Timestamps to Date objects (handle various formats)
      if (data.meta?.createdAt) {
        data.meta.createdAt = convertToDate(data.meta.createdAt);
      }
      if (data.meta?.updatedAt) {
        data.meta.updatedAt = convertToDate(data.meta.updatedAt);
      }

      return data as MasterProfile;
    }

    return null;
  } catch (error) {
    console.error('Error loading profile:', error);
    throw error;
  }
}

/**
 * Saves user profile to Firestore
 * @param userId - Firebase user ID
 * @param profile - MasterProfile object to save
 */
export async function saveProfile(userId: string, profile: MasterProfile): Promise<void> {
  try {
    const docRef = doc(db, 'masterProfiles', userId);
    
    // Prepare profile with timestamps
    const profileToSave = {
      ...profile,
      meta: {
        ...profile.meta,
        updatedAt: Timestamp.now(),
        createdAt: profile.meta?.createdAt || Timestamp.now(),
      },
    };

    await setDoc(docRef, profileToSave, { merge: true });
    console.log('Profile saved successfully');
  } catch (error) {
    console.error('Error saving profile:', error);
    throw error;
  }
}

/**
 * Updates specific fields in user profile
 * @param userId - Firebase user ID
 * @param updates - Partial profile object with fields to update
 */
export async function updateProfile(
  userId: string,
  updates: Partial<MasterProfile>
): Promise<void> {
  try {
    const docRef = doc(db, 'masterProfiles', userId);
    
    const updateData = {
      ...updates,
      'meta.updatedAt': Timestamp.now(),
    };

    await updateDoc(docRef, updateData);
    console.log('Profile updated successfully');
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
}

/**
 * Creates a new empty profile for a user
 * @param userId - Firebase user ID
 * @param email - User's email address
 * @returns Promise resolving to new MasterProfile
 */
export async function createProfile(userId: string, email: string): Promise<MasterProfile> {
  const newProfile: MasterProfile = {
    identity: { email },
    consent: {},
    skin: {},
    logistics: {},
    history: {
      conversations: [],
      purchases: [],
      sampleFeedback: [],
      emailsSent: [],
    },
    safety: {},
    segment: {
      customerStage: 'visitor',
    },
    meta: {
      createdAt: new Date(),
      updatedAt: new Date(),
      profileVersion: '1.0',
    },
  };

  await saveProfile(userId, newProfile);
  return newProfile;
}

/**
 * Checks if profile exists in Firestore
 * @param userId - Firebase user ID
 * @returns Promise resolving to boolean
 */
export async function profileExists(userId: string): Promise<boolean> {
  try {
    const docRef = doc(db, 'masterProfiles', userId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    console.error('Error checking profile existence:', error);
    return false;
  }
}

/**
 * Calculates profile completion percentage based on weighted fields
 * Used to determine phase transitions (< 50% = Profile Building, >= 50% = Product Recommendations)
 * 
 * @param profile - User's master profile (can be partial or empty)
 * @returns Completion percentage (0-100)
 */
export function calculateProfileCompletion(profile: MasterProfile | null | undefined): number {
  if (!profile) return 0;
  
  let score = 0;
  // Weights rebalanced: skincare-relevant fields weighted heavily,
  // logistics deprioritized since INHOUSE_SELLING is off and no product shipping.
  // Target: user reaches 50% with ~4 core skincare questions (email + skin type + concerns + age).
  const weights = {
    identity: {
      email: 10,
      ageGroup: 10,
      name: 5,
    },
    skin: {
      type: 20,
      concerns: 15,
      allergies: 10,
      routineGoals: 5,
    },
    routine: {
      currentProducts: 5,
      preferredBrands: 5,
      budget: 5,
    },
    logistics: {
      address1: 5,
      city: 3,
      postalCode: 2,
    },
  };
  
  // Identity (25 points max)
  if (profile.identity?.email) score += weights.identity.email;
  if (profile.identity?.ageGroup) score += weights.identity.ageGroup;
  if (profile.identity?.name) score += weights.identity.name;
  
  // Skin (50 points max)
  if (profile.skin?.type) score += weights.skin.type;
  if (profile.skin?.concerns?.length) score += weights.skin.concerns;
  if (profile.skin?.allergies?.length) score += weights.skin.allergies;
  const skinGoals = (profile.skin as Record<string, unknown>)?.skinGoals;
  if (profile.skin?.routineGoals?.length || (Array.isArray(skinGoals) && (skinGoals as unknown[]).length > 0)) score += weights.skin.routineGoals;
  
  // Routine & Preferences (15 points max — newly weighted)
  if (profile.skin?.currentProducts?.length) score += weights.routine.currentProducts;
  if (profile.skin?.preferredBrands?.length) score += weights.routine.preferredBrands;
  if (profile.skin?.budgetMonthlyUSD != null) score += weights.routine.budget;
  
  // Logistics (10 points max — deprioritized, no active shipping)
  if (profile.logistics?.address1) score += weights.logistics.address1;
  if (profile.logistics?.city) score += weights.logistics.city;
  if (profile.logistics?.postalCode) score += weights.logistics.postalCode;
  
  return Math.min(100, score);
}

/** Promotion badge types for sponsored/featured products */
import type { AffiliateSource } from '../types/product';

type PromotionBadge = 'sponsored' | 'featured' | 'seasonal' | 'new' | 'editors-pick';

/**
 * Type for Editor's Pick product with score and reason
 */
export interface EditorPick {
  id: string;
  title: string;
  brand: string;
  category: string;
  priceUSD: number;
  priceKRW?: number;
  image: string;
  url: string;
  description: string;
  keyIngredients: string;
  score: number;
  reason: string;
  matchedConcerns?: string[];
  skinTypeMatch: string;
  /** Promotion badge type (if promoted) */
  promotionBadge?: PromotionBadge;
  /** Promotion name for tooltip */
  promotionName?: string;
  
  // Affiliate tracking fields
  /** Affiliate URL for monetization (Amazon, Coupang, etc.) */
  affiliateUrl?: string;
  /** Source of affiliate link */
  affiliateSource?: AffiliateSource;
  /** Internal tracking ID for click attribution */
  trackingId?: string;
  /** Cost-per-click bid for sponsored products */
  cpcBid?: number;
  /** Revenue share percentage (e.g., 0.07 = 7%) */
  revenueShare?: number;
  /** Whether this is a paid/sponsored placement */
  isPaidPlacement?: boolean;
}

/**
 * Ranks products based on user profile with safety filtering and multi-factor scoring
 * Algorithm: Safety filter (allergies, skin type, pregnancy) → Score → Sort → Top K
 * 
 * @param profile - User's master profile
 * @param catalog - Array of products to rank
 * @param topK - Number of top products to return (default: 5)
 * @param embeddingSimilarityMap - Optional map of productId → vector similarity boost (0-0.4)
 * @returns Array of top-ranked products with scores and reasons
 */
export function rankEditorPicks(
  profile: MasterProfile,
  catalog: any[],
  topK: number = 5,
  embeddingSimilarityMap?: Map<string, number>
): EditorPick[] {
  if (!catalog || catalog.length === 0) {
    console.warn('rankEditorPicks: Empty catalog provided');
    return [];
  }

  // Extract profile data
  const skinType = profile.skin?.type?.toLowerCase() || '';
  const concerns = profile.skin?.concerns || [];
  const allergies = profile.skin?.allergies || [];
  const skinGoals = profile.skin?.skinGoals || [];
  const budgetMonthlyUSD = profile.skin?.budgetMonthlyUSD || 100;
  const isPregnant = profile.identity?.pregnant || false;
  
  // Extract user age (can be string "30" or number 30)
  const rawAge = profile.identity?.age;
  const userAge = typeof rawAge === 'number' ? rawAge : 
                  typeof rawAge === 'string' ? parseInt(rawAge, 10) : undefined;
  
  // Get user's current products/cart for routine scoring
  const currentProducts = profile.skin?.currentProducts || [];
  const ownedCategories = profile.skin?.ownedCategories || [];
  
  // Extract categories from currentProducts (format: "Product Name [category]")
  const extractedCategories = currentProducts
    .map((p: string) => {
      const match = p.match(/\[(.*)\]/);
      return match ? match[1].toLowerCase() : '';
    })
    .filter(Boolean);
  
  // Combine owned categories with extracted ones
  const userCategories = [...new Set([...ownedCategories.map(c => c.toLowerCase()), ...extractedCategories])];
  const isSensitive = skinType === 'sensitive';

  // K-beauty affinity detection: check brands, country, and interests
  const KBEAUTY_BRANDS = ['cosrx', 'sk-ii', 'klairs', 'missha', 'purito', 'laneige', 'innisfree', 'beauty of joseon', 'etude', 'sulwhasoo', 'banila co', 'by wishtrend', 'krave beauty'];
  const preferredBrands = (profile.skin?.preferredBrands || []).map(b => b.toLowerCase());
  const interestedBrands = (profile.activity?.interests?.interestedBrands || []).map(b => b.toLowerCase());
  const userCountry = (profile.identity?.country || (typeof profile.identity?.countryConfirmed === 'string' ? profile.identity.countryConfirmed : '') || '').toUpperCase();
  const hasKBeautyBrand = [...preferredBrands, ...interestedBrands].some(b => KBEAUTY_BRANDS.some(kb => b.includes(kb)));
  const hasKBeautyAffinity = hasKBeautyBrand || userCountry === 'KR';

  // Safety filtering
  const safeCatalog = catalog.filter((product) => {
    // Check allergies in full ingredient list
    if (allergies.length > 0) {
      const ingredientsFull = (product.ingredientsFull || '').toLowerCase();
      for (const allergy of allergies) {
        if (ingredientsFull.includes(allergy.toLowerCase())) {
          return false; // Exclude if contains allergy
        }
      }
    }

    // Check skin type match
    // skinTypeMatch can be an array (from catalog normalization) or a string
    const skinTypeMatchArr = Array.isArray(product.skinTypeMatch)
      ? product.skinTypeMatch.map((s: string) => s.toLowerCase())
      : (product.skinTypeMatch || '').toString().toLowerCase().split('|').filter(Boolean);
    if (skinType && skinTypeMatchArr.length > 0 && !skinTypeMatchArr.some((st: string) => st.includes(skinType))) {
      return false; // Exclude if skin type mismatch
    }

    // Check pregnancy restrictions
    // avoidIf can be an array (from catalog normalization) or a string
    const avoidIf = Array.isArray(product.avoidIf) 
      ? product.avoidIf.map((s: string) => s.toLowerCase())
      : (product.avoidIf || '').toString().toLowerCase().split('|').filter(Boolean);
    
    if (isPregnant && avoidIf.some((item: string) => item.includes('pregnancy'))) {
      return false;
    }

    // Check sensitivity restrictions
    if (isSensitive && avoidIf.some((item: string) => item.includes('sensitive-very'))) {
      return false;
    }

    return true; // Product is safe
  });

  if (safeCatalog.length === 0) {
    console.warn('rankEditorPicks: All products filtered out by safety checks');
    return [];
  }

  // Multi-factor scoring
  const scoredProducts = safeCatalog.map((product) => {
    let score = 0;
    const matchedConcerns: string[] = [];

    // 1. Concern matching (+0.2 per match, max +0.5)
    // concernsMatch can be an array (from catalog normalization) or a string
    const productConcerns = Array.isArray(product.concernsMatch)
      ? product.concernsMatch.map((s: string) => s.toLowerCase())
      : (product.concernsMatch || '').toString().toLowerCase().split('|').filter(Boolean);
    for (const concern of concerns) {
      if (productConcerns.some((pc: string) => pc.includes(concern.toLowerCase()))) {
        score += 0.2;
        matchedConcerns.push(concern);
      }
    }
    score = Math.min(score, 0.5); // Cap concern score

    // 2. Goal matching (+0.15 per match)
    // functionalCategories can be an array (from catalog normalization) or a string
    const productGoals = Array.isArray(product.functionalCategories)
      ? product.functionalCategories.map((s: string) => s.toLowerCase())
      : (product.functionalCategories || '').toString().toLowerCase().split('|').filter(Boolean);
    for (const goal of skinGoals) {
      if (productGoals.some((pg: string) => pg.includes(goal.toLowerCase()))) {
        score += 0.15;
      }
    }

    // 3. Skin type exact match (+0.15)
    // skinTypeMatch can be an array (from catalog normalization) or a string
    const skinTypeMatchArray = Array.isArray(product.skinTypeMatch)
      ? product.skinTypeMatch.map((s: string) => s.toLowerCase())
      : (product.skinTypeMatch || '').toString().toLowerCase().split('|').filter(Boolean);
    if (skinType && skinTypeMatchArray.some((st: string) => st.includes(skinType))) {
      score += 0.15;
    }

    // 4. Popularity score (+0.0 to +0.3)
    const popularityScore = product.popularityScore || 0;
    score += Math.min(popularityScore / 1000, 0.3);

    // 5. Budget fit
    if (product.priceUSD <= budgetMonthlyUSD * 0.3) {
      score += 0.05; // Affordable
    } else if (product.priceUSD > budgetMonthlyUSD) {
      score -= 0.1; // Too expensive
    }

    // 6. Paid placement boost
    if (product.isPaidPlacement) {
      score += 1.0; // Significant boost for paid placement
    }

    // 7. NEW: Ingredient-based scoring (+0 to +0.5)
    // Extract keyIngredients from product (can be string or array)
    const keyIngredients = Array.isArray(product.keyIngredients)
      ? product.keyIngredients
      : (product.keyIngredients || '').toString().split('|').filter(Boolean);
    
    const ingredientResult = calculateIngredientScore(keyIngredients, concerns);
    score += ingredientResult.scoreBoost;
    
    // 8. Routine-based scoring (-0.1 to +0.2)
    const productCategory = product.category || '';
    const routineBoost = calculateRoutineScore(productCategory, userCategories);
    score += routineBoost;
    
    // 9. NEW: Concern-based category boost (+0 to +0.25)
    // Boosts products in categories that address user's specific concerns
    const concernRoutineBoost = calculateConcernRoutineBoost(productCategory, concerns, userCategories);
    score += concernRoutineBoost;

    // 10. NEW: Seasonal boost (+0 to +0.15)
    // Boosts products that are seasonally appropriate (hydrating in winter, lightweight in summer)
    const productTags = Array.isArray(product.tags) 
      ? product.tags 
      : (product.tags || '').toString().split('|').filter(Boolean);
    const seasonalResult = calculateSeasonalBoost(productCategory, keyIngredients, productConcerns);
    score += seasonalResult.boost;

    // 11. NEW: Age-appropriate boost (-0.1 to +0.15)
    // Boosts products that are age-appropriate (anti-aging for 30+, acne for teens)
    const ageResult = calculateAgeBoost(userAge, keyIngredients, productConcerns);
    score += ageResult.boost;

    // 12. Vector similarity boost (+0 to +0.4)
    // Uses pre-computed cosine similarity between profile embedding and product embedding
    let vectorBoost = 0;
    if (embeddingSimilarityMap) {
      const productId = product.id || product.sku || '';
      vectorBoost = embeddingSimilarityMap.get(productId) || 0;
      score += vectorBoost;
    }

    // 13. K-beauty affinity boost (+0 to +0.25)
    // Boosts K-beauty products when user has shown K-beauty interest
    if (product.isKBeauty && hasKBeautyAffinity) {
      score += 0.25;
    }

    // Generate reason (Emotional, cosmetic-friendly Korean)
    let reason = '';
    const reasonParts: string[] = [];
    
    // Emotional concern-based phrases (priority #1)
    const concernEmotions: Record<string, string> = {
      'acne': '트러블 진정에 효과적',
      'pore': '모공 케어의 정석',
      'wrinkle': '탄력있는 피부결로',
      'hydration': '속부터 차오르는 수분감',
      'brightening': '맑고 환한 피부톤',
      'sensitive': '자극 없이 순하게',
      'dark spot': '잡티 없는 깨끗함',
      'oiliness': '하루종일 보송보송',
      'dryness': '푸석함에 촉촉함을',
      'redness': '붉은기 진정 케어',
      'aging': '시간을 되돌리는 케어',
    };
    
    for (const concern of matchedConcerns) {
      const lowerConcern = concern.toLowerCase();
      for (const [key, phrase] of Object.entries(concernEmotions)) {
        if (lowerConcern.includes(key) || key.includes(lowerConcern)) {
          reasonParts.push(phrase);
          break;
        }
      }
    }
    
    // If no concern match, add ingredient-based reason
    if (reasonParts.length === 0) {
      const ingredientReason = generateIngredientReason(ingredientResult.matchedIngredients, 'ko');
      if (ingredientReason) {
        reasonParts.push(ingredientReason);
      }
    }
    
    // Add seasonal context if applicable
    if (seasonalResult.reason) {
      reasonParts.push(seasonalResult.reason);
    }
    
    // Add age context if applicable
    if (ageResult.reason) {
      reasonParts.push(ageResult.reason);
    }
    
    // Fallback skin type phrase
    if (reasonParts.length === 0 && skinType) {
      const skinTypePhrases: Record<string, string> = {
        'oily': '번들거림 없이 산뜻하게',
        'dry': '푸석한 피부에 촉촉함을',
        'combo': '복합 피부의 균형을 맞춰요',
        'sensitive': '민감한 피부도 편안하게',
        'normal': '건강한 피부 유지에 딱!',
      };
      reasonParts.push(skinTypePhrases[skinType] || `${skinType} 피부에 적합`);
    }
    
    // Default fallback
    if (reasonParts.length === 0) {
      reason = '당신의 피부에 추천합니다';
    } else {
      reason = reasonParts.slice(0, 2).join(', ');
    }

    // Determine promotion badge
    let promotionBadge: PromotionBadge | undefined;
    if (product.isPaidPlacement) {
      promotionBadge = 'editors-pick';
    } else if (product.promotionBadge) {
      promotionBadge = product.promotionBadge as PromotionBadge;
    }

    return {
      ...product,
      score: Math.round(score * 100), // Convert to percentage
      reason,
      matchedConcerns,
      promotionBadge,
      promotionName: product.promotionName,
    } as EditorPick;
  });

  // Sort by score descending and return top K
  const topPicks = scoredProducts
    .sort((a, b) => b.score - a.score)
    .slice(0, topK);

  console.log(`rankEditorPicks: Ranked ${safeCatalog.length} products, returning top ${topPicks.length}`);
  return topPicks;
}

/**
 * Merges two profile objects together, with source overwriting target
 * Deep merges nested objects, handles arrays properly
 * 
 * @param target - Base profile to merge into
 * @param source - Profile updates to apply
 * @returns Merged MasterProfile
 */
export function mergeProfile(
  target: MasterProfile | Partial<MasterProfile>,
  source: Partial<MasterProfile>
): MasterProfile {
  const merged = { ...target } as MasterProfile;

  for (const key of Object.keys(source) as Array<keyof MasterProfile>) {
    const sourceValue = source[key];
    const targetValue = merged[key];

    if (sourceValue === undefined || sourceValue === null) {
      continue; // Skip undefined/null values
    }

    if (Array.isArray(sourceValue)) {
      // Union-merge arrays: combine existing items with new ones (case-insensitive dedup)
      if (Array.isArray(targetValue)) {
        const existingLower = new Set(
          (targetValue as unknown[]).map((item: unknown) => typeof item === 'string' ? item.toLowerCase() : String(item))
        );
        const merged2 = [...(targetValue as unknown[])];
        for (const item of sourceValue) {
          const key = typeof item === 'string' ? item.toLowerCase() : String(item);
          if (!existingLower.has(key)) {
            merged2.push(item);
            existingLower.add(key);
          }
        }
        (merged as any)[key] = merged2;
      } else {
        (merged as any)[key] = sourceValue;
      }
    } else if (typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
      // Deep merge objects
      (merged as any)[key] = {
        ...(targetValue as object || {}),
        ...(sourceValue as object),
      };
    } else {
      // Primitive values - use source
      (merged as any)[key] = sourceValue;
    }
  }

  return ensureShape(merged);
}

/**
 * Applies a diff object to an existing profile
 * Diff contains paths like 'skin.type' with old/new values
 * 
 * @param profile - Existing profile to update
 * @param diff - Object containing field paths with old/new values
 * @returns Updated MasterProfile
 */
export function applyProfileDiff(
  profile: MasterProfile,
  diff: Record<string, { old?: unknown; new?: unknown; remove?: unknown[] }>
): MasterProfile {
  if (!diff || Object.keys(diff).length === 0) {
    return profile;
  }

  const updated = { ...profile };

  for (const [path, change] of Object.entries(diff)) {
    const hasNew = change.new !== undefined && change.new !== null;
    const hasRemove = Array.isArray(change.remove) && change.remove.length > 0;
    if (!hasNew && !hasRemove) {
      continue; // Skip entries with no actionable data
    }

    const parts = path.split('.');
    let current: any = updated;

    // Navigate to parent of target field
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current[part]) {
        current[part] = {};
      }
      current = current[part];
    }

    const finalKey = parts[parts.length - 1];

    // Step 1: Apply removals (if current value is an array)
    if (hasRemove && Array.isArray(current[finalKey])) {
      const removeLower = new Set(
        (change.remove as unknown[]).map((item: unknown) => typeof item === 'string' ? item.toLowerCase() : String(item))
      );
      current[finalKey] = (current[finalKey] as unknown[]).filter((item: unknown) => {
        const key = typeof item === 'string' ? item.toLowerCase() : String(item);
        return !removeLower.has(key);
      });
    }

    // Step 2: Union-merge new items (or set scalar value)
    if (hasNew) {
      if (Array.isArray(change.new) && Array.isArray(current[finalKey])) {
        const existingLower = new Set(
          (current[finalKey] as unknown[]).map((item: unknown) => typeof item === 'string' ? item.toLowerCase() : String(item))
        );
        const merged = [...(current[finalKey] as unknown[])];
        for (const item of change.new as unknown[]) {
          const key = typeof item === 'string' ? item.toLowerCase() : String(item);
          if (!existingLower.has(key)) {
            merged.push(item);
            existingLower.add(key);
          }
        }
        current[finalKey] = merged;
      } else {
        current[finalKey] = change.new;
      }
    }
  }

  return ensureShape(updated);
}

/**
 * Cleans a profile object for Firestore storage
 * Removes undefined values, converts Date objects to Timestamps
 * 
 * @param profile - Profile to clean
 * @returns Cleaned profile safe for Firestore
 */
export function cleanForFirestore(profile: MasterProfile): Record<string, any> {
  const cleaned: Record<string, any> = {};

  function cleanValue(value: any): any {
    if (value === undefined) {
      return null; // Firestore doesn't accept undefined
    }
    if (value instanceof Date) {
      return Timestamp.fromDate(value);
    }
    if (Array.isArray(value)) {
      return value.map(cleanValue).filter((v) => v !== undefined);
    }
    if (value !== null && typeof value === 'object') {
      return cleanObject(value);
    }
    return value;
  }

  function cleanObject(obj: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        result[key] = cleanValue(value);
      }
    }
    return result;
  }

  // Clean each section of the profile
  for (const [key, value] of Object.entries(profile)) {
    if (value !== undefined) {
      cleaned[key] = cleanValue(value);
    }
  }

  return cleaned;
}
